import {
  useModal,
  useToast,
  colors,
  icons,
  opacityTransition,
  sizing,
  getTestAttributes,
} from '@chillibean/component-library';
import { Icon } from '@iconify/react';
import { clsx } from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Loader, LoaderVariant, Tag } from '../../../../components/presentational';
import { DisplayNameType, useDisplayName, useStudyEventDefinitionsFilteredByStudyArm } from '../../../../hooks';
import { StudyEventDefinition, StudyProcedureDefinition, StudyEventProcedure } from '../../../../models';
import { updateProtocol } from '../../../../redux/actions';
import {
  emptyStudyEventDefinitions,
  emptyStudyProcedureDefinitions,
  firstInArray,
  isTemplate,
  isUndefined,
  lastInArray,
} from '../../../../utils';
import { RenameModal } from '../container';
import { Pill } from './Pill';
import { TableDataCell } from './TableDataCell';

const STUDY_SCHEDULER_CONSTANTS = {
  columnHeader: {
    height: 68,
    width: 120,
  },
  rowHeader: {
    height: 88,
    width: 160,
  },
};

export function StudySchedulerTable(props) {
  const {
    addStudyEventProcedure,
    columnTag,
    numberOfExtraCells,
    openStudyEventDefinitionModal,
    openStudyProcedureDefinitionModal,
    removeStudyEventProcedure,
    rowTag,
    tableData,
  } = props;
  const { studyEventDefinitions, studyEventProcedures, studyProcedureDefinitions } = tableData;

  // HOOKS
  // Store
  const dispatch = useDispatch();
  const { data: protocol, loading } = useSelector((state) => state.protocolReducer.protocol);
  // Custom
  const { getDisplayName } = useDisplayName();
  const {
    closeModal: closeStudyEventRenameModal,
    data: selectedStudyEventData,
    modalOpen: studyEventRenameModalOpen,
    openModal: openStudyEventRenameModal,
  } = useModal();
  const {
    closeModal: closeStudyProcedureRenameModal,
    data: selectedStudyProcedureData,
    modalOpen: studyProcedureRenameModalOpen,
    openModal: openStudyProcedureRenameModal,
  } = useModal();
  const { showErrorToast, showSuccessToast } = useToast();
  const { filteredStudyEventDefinitions } = useStudyEventDefinitionsFilteredByStudyArm({ studyEventDefinitions });

  const tableColumns = [
    ...filteredStudyEventDefinitions,
    ...emptyStudyEventDefinitions(
      numberOfExtraCells.columns,
      getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular,
    ),
  ];

  const tableRows = [
    ...studyProcedureDefinitions,
    ...emptyStudyProcedureDefinitions(
      numberOfExtraCells.rows,
      getDisplayName(DisplayNameType.STUDY_PROCEDURE).humanized.singular,
    ),
  ];

  const studyEventValues = {
    plural: getDisplayName(DisplayNameType.STUDY_EVENT).humanized.plural,
    singular: getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular,
  };

  const studyProcedureValues = {
    plural: getDisplayName(DisplayNameType.STUDY_PROCEDURE).humanized.plural,
    singular: getDisplayName(DisplayNameType.STUDY_PROCEDURE).humanized.singular,
  };

  const generateStudyArmData = (studyArm) => {
    return studyArm || null;
  };

  const handleStudyEventRename = (data) => {
    dispatch(
      updateProtocol({
        identifier: protocol.id,
        onError: showErrorToast,
        onSuccess: () => {
          showSuccessToast('Study Event renamed successfully');
          closeStudyEventRenameModal();
        },
        protocolData: { studyEventDisplayName: data },
      }),
    );
  };

  const handleStudyProcedureRename = (data) => {
    dispatch(
      updateProtocol({
        identifier: protocol.id,
        onError: showErrorToast,
        onSuccess: () => {
          showSuccessToast('Study Procedure renamed successfully');
          closeStudyProcedureRenameModal();
        },
        protocolData: { studyProcedureDisplayName: data },
      }),
    );
  };

  return (
    <>
      <RenameModal
        data={selectedStudyEventData}
        onClose={closeStudyEventRenameModal}
        onSave={handleStudyEventRename}
        title="Update Study Event Name"
        visible={studyEventRenameModalOpen}
      />
      <RenameModal
        data={selectedStudyProcedureData}
        onClose={closeStudyProcedureRenameModal}
        onSave={handleStudyProcedureRename}
        title="Update Study Procedure Name"
        visible={studyProcedureRenameModalOpen}
      />
      <TableContainer>
        <SpaceBeforeTable />
        <Table>
          <TableHead>
            <TableRow>
              <BlankTableHeader
                $height={STUDY_SCHEDULER_CONSTANTS.columnHeader.height}
                $width={STUDY_SCHEDULER_CONSTANTS.columnHeader.width}
              >
                <Tag.Column
                  disabled={protocol?.locked}
                  label={columnTag}
                  loading={loading}
                  onClick={() => openStudyEventRenameModal({ ...studyEventValues })}
                />
                <Tag.Row
                  disabled={protocol?.locked}
                  label={rowTag}
                  loading={loading}
                  onClick={() => openStudyProcedureRenameModal({ ...studyProcedureValues })}
                />
              </BlankTableHeader>
              {tableColumns?.map((studyEventDefinition, index) => {
                const disabled = isTemplate(studyEventDefinition.id) || protocol?.locked;
                const studyArmData = generateStudyArmData(studyEventDefinition?.studyArm);

                return (
                  <TableHeader
                    key={`study-event-definition-${studyEventDefinition.id}`}
                    $height={STUDY_SCHEDULER_CONSTANTS.columnHeader.height}
                    $width={STUDY_SCHEDULER_CONSTANTS.columnHeader.width}
                    className={clsx({
                      'disabled': disabled,
                      'first-column': firstInArray(index),
                      'last-column': lastInArray(index, tableColumns.length),
                      'non-filled': firstInArray(studyEventDefinition.id),
                    })}
                    disabled={disabled}
                    onClick={() => !disabled && openStudyEventDefinitionModal(studyEventDefinition)}
                    {...getTestAttributes(`study-event-definition-${studyEventDefinition?.id}`)}
                  >
                    {loading ? (
                      <Loader variant={LoaderVariant.HEADER} />
                    ) : (
                      <HeaderSpan>
                        <HeaderStudyEventDefinition className="tr-name">
                          {studyEventDefinition.displayName}
                        </HeaderStudyEventDefinition>
                        <StyledEdit className="tr-icon">
                          <Icon height={12} icon={icons.baselineEdit} width={12} />
                        </StyledEdit>
                        {Boolean(studyArmData) && <Pill text={studyArmData.name} />}
                      </HeaderSpan>
                    )}
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows?.map((studyProcedureDefinition, rowIndex) => {
              const disabled = isTemplate(studyProcedureDefinition.id) || protocol?.locked;

              return (
                <TableRow key={`study-procedure-definition-${studyProcedureDefinition.id}`}>
                  <TableRowHeader
                    $height={STUDY_SCHEDULER_CONSTANTS.rowHeader.height}
                    $width={STUDY_SCHEDULER_CONSTANTS.rowHeader.width}
                    className={clsx({
                      'disabled': disabled,
                      'first-row': firstInArray(rowIndex),
                      'last-row': lastInArray(rowIndex, tableRows.length),
                      'non-filled': firstInArray(studyProcedureDefinition.id),
                    })}
                    disabled={disabled}
                    onClick={() => !disabled && openStudyProcedureDefinitionModal(studyProcedureDefinition)}
                    {...getTestAttributes(`study-procedure-definition-${studyProcedureDefinition?.id}`)}
                  >
                    {loading ? (
                      <Loader variant={LoaderVariant.HEADER} />
                    ) : (
                      <HeaderSpan>
                        <StyledEdit className="tr-name">{studyProcedureDefinition.displayName}</StyledEdit>
                        <StyledEdit className="tr-icon">
                          <Icon height={12} icon={icons.baselineEdit} width={12} />
                        </StyledEdit>
                      </HeaderSpan>
                    )}
                  </TableRowHeader>
                  {tableColumns?.map((studyEventDefinition, columnIndex) => {
                    let filled = false;
                    const currentStudyProcedureDefinition = studyProcedureDefinitions[rowIndex];
                    const currentStudyEventDefinition = filteredStudyEventDefinitions[columnIndex];
                    const currentActive = currentStudyProcedureDefinition && currentStudyEventDefinition;
                    const active = !isUndefined(currentActive);
                    const cellData = {
                      studyEventDefinitionId: 0,
                      studyEventProcedureId: 0,
                      studyProcedureDefinitionId: 0,
                    };
                    if (active) {
                      const curStudyEventProcedure = studyEventProcedures.find((studyEventProcedure) => {
                        return (
                          studyEventProcedure.studyEventDefinitionId === currentStudyEventDefinition.id &&
                          studyEventProcedure.studyProcedureDefinitionId === currentStudyProcedureDefinition.id
                        );
                      });
                      filled = !isUndefined(curStudyEventProcedure);
                      cellData.studyProcedureDefinitionId = studyProcedureDefinition.id;
                      cellData.studyEventDefinitionId = studyEventDefinition.id;
                      if (filled) cellData.studyEventProcedureId = curStudyEventProcedure.id;
                    }

                    return (
                      <TableData
                        key={`study-event-procedure-${studyProcedureDefinition.id}-${studyEventDefinition.id}`}
                        className={clsx({
                          'last-column': lastInArray(columnIndex, tableColumns.length),
                          'last-row': lastInArray(rowIndex, tableRows.length),
                          'last-row-and-column':
                            lastInArray(rowIndex, tableRows.length) && lastInArray(columnIndex, tableColumns.length),
                        })}
                      >
                        <TableDataCell
                          active={active}
                          cellData={cellData}
                          disabled={protocol?.locked}
                          filled={filled}
                          loading={loading}
                          onAppend={addStudyEventProcedure}
                          onRemove={removeStudyEventProcedure}
                        />
                      </TableData>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <SpaceAfterTable />
      </TableContainer>
    </>
  );
}
const TableContainer = styled.div`
  overflow: scroll;
  display: flex;
  padding: 20px;
`;

const SpaceBeforeTable = styled.div`
  margin-left: 39px;
`;

const SpaceAfterTable = styled.div`
  margin-right: 39px;
`;

const Table = styled.table`
  border-collapse: separate;
`;

const TableHead = styled.thead`
  background-color: ${colors.grey.secondary};
  padding: ${sizing.small};
`;

const TableBody = styled.tbody``;

const HeaderSpan = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const HeaderStudyEventDefinition = styled.div`
  margin-bottom: 5px;
`;

const TableHeader = styled.th`
  border-top: 0;
  border: 2px solid ${colors.grey.tertiary};
  border-right: 0;
  padding: ${sizing.small};
  perspective: 1px;
  height: ${(props) => props.$height}px;
  text-align: center;
  width: ${(props) => props.$width}px;
  vertical-align: middle;
  color: ${colors.blue.primary};
  padding: ${sizing.small};
  cursor: ${(props) => (props.disabled ? 'auto' : 'pointer')};

  &.first-column {
    border-top-left-radius: ${sizing.xsmall};
  }

  &.last-column {
    border-top-right-radius: ${sizing.xsmall};
    border-right: 2px solid ${colors.grey.tertiary};
  }

  &.non-filled,
  &.disabled {
    color: ${colors.grey.primaryAlpha('20')};
    background-color: ${colors.grey.secondary};
  }

  &.non-filled.disabled {
    color: ${colors.grey.primaryAlpha('10')};
  }

  &:not(.non-filled):not(.disabled):hover {
    background-color: ${colors.grey.quinery};
  }

  & div.tr-icon {
    display: none;
    opacity: 0;
  }

  &:hover:not(.disabled) {
    & div.tr-name {
      display: none;
      opacity: 0;
      transition: ${opacityTransition};
    }

    & div.tr-icon {
      display: flex;
      opacity: 1;
      transition: ${opacityTransition};
    }
  }
`;

const StyledEdit = styled.div``;

const BlankTableHeader = styled.th`
  border-top: 0;
  height: ${(props) => props.$height}px;
  width: ${(props) => props.$width}px;
  text-align: center;
  background-color: ${colors.white.primary};
  position: relative;
`;

const TableRow = styled.tr``;

const TableRowHeader = styled.th`
  cursor: ${(props) => (props.disabled ? 'auto' : 'pointer')};
  height: ${(props) => props.$height}px;
  width: ${(props) => props.$width}px;
  float: left;
  line-height: 1;
  border-top: 0;
  border: 2px solid ${colors.grey.tertiary};
  border-bottom: 0;
  background-color: ${colors.grey.secondary};
  padding: ${sizing.large};
  width: ${(props) => props.$width}px;
  color: ${colors.blue.primary};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;

  &.first-row {
    border-top-left-radius: ${sizing.xsmall};
  }

  &.last-row {
    border-bottom: 2px solid ${colors.grey.tertiary};
    border-bottom-left-radius: ${sizing.xsmall};
  }

  &.non-filled,
  &.disabled {
    color: ${colors.grey.primaryAlpha('20')};
    background-color: ${colors.grey.secondary};
  }

  &.non-filled.disabled {
    color: ${colors.grey.primaryAlpha('10')};
  }

  &:not(.non-filled):not(.disabled):hover {
    background-color: ${colors.grey.quinery};
  }

  & div.tr-icon {
    display: none;
    opacity: 0;
  }

  &:hover:not(.disabled) {
    & div.tr-name {
      display: none;
      opacity: 0;
      transition: ${opacityTransition};
    }

    & div.tr-icon {
      display: flex;
      opacity: 1;
      transition: ${opacityTransition};
    }
  }
`;

const TableData = styled.td`
  border-top: 0;
  padding: ${sizing.small};

  &.last-row {
    border-bottom: 2px solid ${colors.grey.tertiary};
  }

  &.last-column {
    border-right: 2px solid ${colors.grey.tertiary};
  }

  &.last-row-and-column {
    border-bottom-right-radius: ${sizing.xsmall};
  }
`;

StudySchedulerTable.propTypes = {
  addStudyEventProcedure: PropTypes.func.isRequired,
  columnTag: PropTypes.string.isRequired,
  numberOfExtraCells: PropTypes.shape({
    columns: PropTypes.number.isRequired,
    rows: PropTypes.number.isRequired,
  }).isRequired,
  openStudyEventDefinitionModal: PropTypes.func.isRequired,
  openStudyProcedureDefinitionModal: PropTypes.func.isRequired,
  removeStudyEventProcedure: PropTypes.func.isRequired,
  rowTag: PropTypes.string.isRequired,
  tableData: PropTypes.shape({
    studyEventDefinitions: PropTypes.arrayOf(PropTypes.instanceOf(StudyEventDefinition)).isRequired,
    studyEventProcedures: PropTypes.arrayOf(PropTypes.instanceOf(StudyEventProcedure)).isRequired,
    studyProcedureDefinitions: PropTypes.arrayOf(PropTypes.instanceOf(StudyProcedureDefinition)).isRequired,
  }).isRequired,
};

export default StudySchedulerTable;
